import React from "react";
import { Link } from "react-router-dom";
import { Download, MessageCircle, Star, Sparkles, TrendingUp } from "lucide-react";
import { MOCK_ORDERS, MOCK_STATS_VENDOR } from "../../data/mock";
import { StatusBadge } from "../customer/CustomerHome";
import { useAuth } from "../../context/AuthContext";

export default function VendorHome() {
  const { user } = useAuth();
  const active = MOCK_ORDERS.filter((o) => o.status !== "delivered");

  return (
    <div className="space-y-6" data-testid="vendor-home">
      <div className="overflow-hidden rounded-xl border border-neutral-200 bg-neutral-950 p-6 text-white grain md:p-8">
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div>
            <div className="text-xs font-semibold uppercase tracking-widest text-lime-400">Vendor · Delhi NCR</div>
            <h1 className="mt-2 font-display text-3xl font-bold">Hi {user?.name?.split(" ")[0] || "there"} 👋</h1>
            <p className="mt-1 text-sm text-neutral-300">You have {active.length} active orders and {MOCK_STATS_VENDOR.newLeads} new bid opportunities today.</p>
          </div>
          <Link to="/vendor/bidding" className="btn-offset inline-flex items-center gap-2 rounded-md bg-lime-400 px-4 py-2 text-sm font-bold text-neutral-950 hover:bg-lime-300" data-testid="vh-open-bids">
            <Sparkles size={16} /> Open bid requests
          </Link>
        </div>
      </div>

      <div className="grid gap-4 md:grid-cols-4">
        <Stat label="New leads today" v={MOCK_STATS_VENDOR.newLeads} sub="+2 vs yesterday" />
        <Stat label="In production" v={MOCK_STATS_VENDOR.inProduction} sub="Pickup queued" />
        <Stat label="Today's earnings" v={`₹${MOCK_STATS_VENDOR.todayEarnings.toLocaleString("en-IN")}`} sub="Escrow: ₹12,500" />
        <Stat label="Rating" v={`${MOCK_STATS_VENDOR.rating} ★`} sub={`${MOCK_STATS_VENDOR.reviews} reviews`} />
      </div>

      <section>
        <div className="flex items-end justify-between">
          <h2 className="font-display text-xl font-bold">Incoming orders</h2>
          <div className="text-xs font-mono text-neutral-500">Auto-refreshes</div>
        </div>
        <div className="mt-3 space-y-2">
          {active.map((o) => (
            <div key={o.id} className="rounded-lg border border-neutral-200 bg-white p-4" data-testid={`vh-order-${o.id}`}>
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                  <div className="font-mono text-xs text-neutral-500">#{o.id} · Placed {o.placed}</div>
                  <div className="mt-1 font-display text-lg font-bold">{o.product}</div>
                  <div className="text-xs text-neutral-500">{o.customer} · {o.location} · {o.eta}</div>
                </div>
                <div className="text-right">
                  <div className="font-mono text-lg font-bold">₹{o.amount.toLocaleString("en-IN")}</div>
                  <StatusBadge status={o.status} />
                </div>
              </div>
              <div className="mt-3 flex flex-wrap gap-2">
                <button className="rounded-md bg-neutral-950 px-3 py-1.5 text-xs font-bold text-white hover:bg-neutral-800" data-testid={`vh-mark-ready-${o.id}`}>Mark ready</button>
                <button className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold hover:border-neutral-900" data-testid={`vh-file-${o.id}`}><Download size={12} /> Download file</button>
                <a href="https://wa.me/919899747172" target="_blank" rel="noopener noreferrer" className="inline-flex items-center gap-1 rounded-md border border-neutral-300 px-3 py-1.5 text-xs font-semibold hover:border-neutral-900" data-testid={`vh-chat-${o.id}`}><MessageCircle size={12} /> Chat customer</a>
              </div>
            </div>
          ))}
        </div>
      </section>

      <section className="rounded-lg border border-neutral-200 bg-white p-5">
        <div className="flex items-center gap-2 text-sm font-semibold"><TrendingUp size={16} className="text-lime-500" /> On-time rate: {MOCK_STATS_VENDOR.onTime}%</div>
        <p className="mt-1 text-xs text-neutral-500">Vendors above 95% get priority bid placement. Keep it up!</p>
      </section>
    </div>
  );
}

function Stat({ label, v, sub }) {
  return (
    <div className="rounded-lg border border-neutral-200 bg-white p-5">
      <div className="text-xs uppercase tracking-widest text-neutral-500">{label}</div>
      <div className="mt-1 font-display text-3xl font-black">{v}</div>
      <div className="mt-1 text-xs text-neutral-500">{sub}</div>
    </div>
  );
}
